// Advanced vector features: boolean ops, path manipulation, gradients, effects, blend, patterns
import polygonClipping from 'polygon-clipping';
import { genId } from './editorHelpers';

// ==================== SHAPE → POLYGON CONVERSION ====================
const ELLIPSE_SEGMENTS = 64;

export const shapeToPolygon = (obj) => {
  if (obj.type === 'rect') {
    const { x, y, w, h } = obj;
    return [[[x, y], [x + w, y], [x + w, y + h], [x, y + h], [x, y]]];
  }
  if (obj.type === 'ellipse') {
    const cx = obj.x + obj.w / 2, cy = obj.y + obj.h / 2;
    const rx = obj.w / 2, ry = obj.h / 2;
    const pts = [];
    for (let i = 0; i <= ELLIPSE_SEGMENTS; i++) {
      const a = (2 * Math.PI * i) / ELLIPSE_SEGMENTS;
      pts.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)]);
    }
    return [pts];
  }
  if (obj.type === 'polygon' && obj.points) {
    const pts = obj.points.split(/\s+/).map(p => {
      const [x, y] = p.split(',').map(Number);
      return [x, y];
    });
    if (pts.length > 0) {
      pts.push([...pts[0]]); // close
      return [pts];
    }
  }
  return null;
};

export const polygonToPath = (coords) => {
  if (!coords || coords.length === 0) return '';
  let d = '';
  coords.forEach(ring => {
    ring.forEach(poly => {
      if (poly.length === 0) return;
      d += `M ${poly[0][0]} ${poly[0][1]} `;
      for (let i = 1; i < poly.length; i++) {
        d += `L ${poly[i][0]} ${poly[i][1]} `;
      }
      d += 'Z ';
    });
  });
  return d.trim();
};

export const polygonBBox = (coords) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  coords.forEach(ring => ring.forEach(poly => poly.forEach(([x, y]) => {
    minX = Math.min(minX, x); minY = Math.min(minY, y);
    maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  })));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
};

// ==================== BOOLEAN OPERATIONS ====================
export const booleanUnite = (objA, objB, style) => {
  const polyA = shapeToPolygon(objA);
  const polyB = shapeToPolygon(objB);
  if (!polyA || !polyB) return null;
  try {
    const result = polygonClipping.union(polyA, polyB);
    const d = polygonToPath(result);
    const bbox = polygonBBox(result);
    return { id: genId(), type: 'path', d, ...bbox, ...style, visible: true, locked: false, opacity: 1, label: 'Union' };
  } catch { return null; }
};

export const booleanSubtract = (objA, objB, style) => {
  const polyA = shapeToPolygon(objA);
  const polyB = shapeToPolygon(objB);
  if (!polyA || !polyB) return null;
  try {
    const result = polygonClipping.difference(polyA, polyB);
    if (result.length === 0) return null;
    const d = polygonToPath(result);
    const bbox = polygonBBox(result);
    return { id: genId(), type: 'path', d, ...bbox, ...style, visible: true, locked: false, opacity: 1, label: 'Subtract' };
  } catch { return null; }
};

export const booleanIntersect = (objA, objB, style) => {
  const polyA = shapeToPolygon(objA);
  const polyB = shapeToPolygon(objB);
  if (!polyA || !polyB) return null;
  try {
    const result = polygonClipping.intersection(polyA, polyB);
    if (result.length === 0) return null;
    const d = polygonToPath(result);
    const bbox = polygonBBox(result);
    return { id: genId(), type: 'path', d, ...bbox, ...style, visible: true, locked: false, opacity: 1, label: 'Intersect' };
  } catch { return null; }
};

export const booleanExclude = (objA, objB, style) => {
  const polyA = shapeToPolygon(objA);
  const polyB = shapeToPolygon(objB);
  if (!polyA || !polyB) return null;
  try {
    const result = polygonClipping.xor(polyA, polyB);
    if (result.length === 0) return null;
    const d = polygonToPath(result);
    const bbox = polygonBBox(result);
    return { id: genId(), type: 'path', d, ...bbox, ...style, visible: true, locked: false, opacity: 1, label: 'Exclude' };
  } catch { return null; }
};

// ==================== PATH / ANCHOR POINT MANIPULATION ====================
export const parseSVGPath = (d) => {
  if (!d) return [];
  const points = [];
  const commands = d.match(/[MLQCZA][^MLQCZA]*/gi) || [];
  let cx = 0, cy = 0;
  commands.forEach(cmd => {
    const type = cmd[0].toUpperCase();
    const nums = cmd.slice(1).trim().split(/[\s,]+/).map(Number).filter(n => !isNaN(n));
    if (type === 'M' && nums.length >= 2) {
      cx = nums[0]; cy = nums[1];
      points.push({ x: cx, y: cy, type: 'move' });
    } else if (type === 'L' && nums.length >= 2) {
      cx = nums[0]; cy = nums[1];
      points.push({ x: cx, y: cy, type: 'line' });
    } else if (type === 'C' && nums.length >= 6) {
      points.push({
        x: nums[4], y: nums[5], type: 'cubic',
        cx1: nums[0], cy1: nums[1], cx2: nums[2], cy2: nums[3]
      });
      cx = nums[4]; cy = nums[5];
    } else if (type === 'Q' && nums.length >= 4) {
      points.push({
        x: nums[2], y: nums[3], type: 'quadratic',
        cx1: nums[0], cy1: nums[1]
      });
      cx = nums[2]; cy = nums[3];
    } else if (type === 'Z') {
      points.push({ type: 'close' });
    }
  });
  return points;
};

export const pointsToSVGPath = (points) => {
  let d = '';
  points.forEach(pt => {
    if (pt.type === 'move') d += `M ${pt.x} ${pt.y} `;
    else if (pt.type === 'line') d += `L ${pt.x} ${pt.y} `;
    else if (pt.type === 'cubic') d += `C ${pt.cx1} ${pt.cy1} ${pt.cx2} ${pt.cy2} ${pt.x} ${pt.y} `;
    else if (pt.type === 'quadratic') d += `Q ${pt.cx1} ${pt.cy1} ${pt.x} ${pt.y} `;
    else if (pt.type === 'close') d += 'Z ';
  });
  return d.trim();
};

export const addAnchorPoint = (pathPoints, segmentIndex, t = 0.5) => {
  if (segmentIndex < 0 || segmentIndex >= pathPoints.length - 1) return pathPoints;
  const p0 = pathPoints[segmentIndex];
  const p1 = pathPoints[segmentIndex + 1];
  const newPoint = { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t, type: 'line' };
  const result = [...pathPoints];
  result.splice(segmentIndex + 1, 0, newPoint);
  return result;
};

export const deleteAnchorPoint = (pathPoints, pointIndex) => {
  if (pathPoints.length <= 2) return pathPoints;
  return pathPoints.filter((_, i) => i !== pointIndex);
};

export const convertAnchorSmooth = (pathPoints, pointIndex) => {
  const pts = [...pathPoints];
  const pt = pts[pointIndex];
  if (!pt) return pts;
  const prev = pts[pointIndex - 1];
  const next = pts[pointIndex + 1];
  if (pt.type === 'line' && prev && next) {
    const dx = (next.x - (prev.x || 0)) / 4;
    const dy = (next.y - (prev.y || 0)) / 4;
    pts[pointIndex] = { ...pt, type: 'cubic', cx1: pt.x - dx, cy1: pt.y - dy, cx2: pt.x + dx, cy2: pt.y + dy };
  } else if (pt.type === 'cubic') {
    pts[pointIndex] = { x: pt.x, y: pt.y, type: 'line' };
  }
  return pts;
};

// ==================== GRADIENT HELPERS ====================
export const createLinearGradient = (id, angle = 0, stops = [{ offset: 0, color: '#0D9488', opacity: 1 }, { offset: 1, color: '#3B82F6', opacity: 1 }]) => {
  const rad = (angle * Math.PI) / 180;
  return {
    id: id || `grad-${genId()}`,
    type: 'linear',
    x1: 50 - 50 * Math.cos(rad), y1: 50 - 50 * Math.sin(rad),
    x2: 50 + 50 * Math.cos(rad), y2: 50 + 50 * Math.sin(rad),
    stops,
    angle
  };
};

export const createRadialGradient = (id, stops = [{ offset: 0, color: '#0D9488', opacity: 1 }, { offset: 1, color: '#3B82F6', opacity: 0.2 }]) => ({
  id: id || `grad-${genId()}`,
  type: 'radial',
  cx: 50, cy: 50, r: 50,
  stops
});

// ==================== SVG FILTER GENERATION ====================
export const generateFilterSVG = (effects, filterId) => {
  if (!effects || effects.length === 0) return null;
  let filterContent = '';
  let prevResult = 'SourceGraphic';

  effects.forEach((fx, i) => {
    const result = `fx-${i}`;
    switch (fx.type) {
      case 'dropShadow':
        filterContent += `<feDropShadow dx="${fx.dx || 3}" dy="${fx.dy || 3}" stdDeviation="${fx.blur || 4}" flood-color="${fx.color || '#000000'}" flood-opacity="${fx.opacity || 0.3}" result="${result}"/>`;
        break;
      case 'blur':
        filterContent += `<feGaussianBlur in="${prevResult}" stdDeviation="${fx.radius || 4}" result="${result}"/>`;
        break;
      case 'glow': {
        filterContent += `<feGaussianBlur in="SourceAlpha" stdDeviation="${fx.radius || 6}" result="glowBlur-${i}"/>`;
        filterContent += `<feFlood flood-color="${fx.color || '#0D9488'}" flood-opacity="${fx.opacity || 0.6}" result="glowColor-${i}"/>`;
        filterContent += `<feComposite in="glowColor-${i}" in2="glowBlur-${i}" operator="in" result="glowComp-${i}"/>`;
        filterContent += `<feMerge result="${result}"><feMergeNode in="glowComp-${i}"/><feMergeNode in="SourceGraphic"/></feMerge>`;
        break;
      }
      case 'innerShadow':
        filterContent += `<feOffset dx="${fx.dx || 2}" dy="${fx.dy || 2}" result="offOut-${i}"/>`;
        filterContent += `<feGaussianBlur in="offOut-${i}" stdDeviation="${fx.blur || 3}" result="blurOut-${i}"/>`;
        filterContent += `<feComposite in="SourceGraphic" in2="blurOut-${i}" operator="atop" result="${result}"/>`;
        break;
      default: break;
    }
    prevResult = result;
  });

  return `<filter id="${filterId}" x="-50%" y="-50%" width="200%" height="200%">${filterContent}</filter>`;
};

export const EFFECT_PRESETS = [
  { name: 'Drop Shadow', effects: [{ type: 'dropShadow', dx: 3, dy: 3, blur: 4, color: '#000000', opacity: 0.25 }] },
  { name: 'Soft Shadow', effects: [{ type: 'dropShadow', dx: 0, dy: 4, blur: 12, color: '#000000', opacity: 0.15 }] },
  { name: 'Blur', effects: [{ type: 'blur', radius: 4 }] },
  { name: 'Teal Glow', effects: [{ type: 'glow', radius: 6, color: '#0D9488', opacity: 0.5 }] },
  { name: 'Blue Glow', effects: [{ type: 'glow', radius: 8, color: '#3B82F6', opacity: 0.4 }] },
  { name: 'Gold Glow', effects: [{ type: 'glow', radius: 6, color: '#D97706', opacity: 0.5 }] },
];

// ==================== BLENDING MODES ====================
export const BLEND_MODES = [
  'normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten',
  'color-dodge', 'color-burn', 'hard-light', 'soft-light',
  'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity'
];

// ==================== BLEND TOOL (Shape Interpolation) ====================
const lerpColor = (c1, c2, t) => {
  const hex = (s) => parseInt(s, 16);
  const r1 = hex(c1.slice(1, 3)), g1 = hex(c1.slice(3, 5)), b1 = hex(c1.slice(5, 7));
  const r2 = hex(c2.slice(1, 3)), g2 = hex(c2.slice(3, 5)), b2 = hex(c2.slice(5, 7));
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const createBlend = (objA, objB, steps = 5) => {
  const results = [];
  const bboxA = { x: objA.x || 0, y: objA.y || 0, w: objA.w || 50, h: objA.h || 50 };
  const bboxB = { x: objB.x || 0, y: objB.y || 0, w: objB.w || 50, h: objB.h || 50 };

  for (let i = 1; i <= steps; i++) {
    const t = i / (steps + 1);
    const fillA = objA.fill && objA.fill !== 'none' ? objA.fill : '#0D9488';
    const fillB = objB.fill && objB.fill !== 'none' ? objB.fill : '#3B82F6';
    results.push({
      id: genId(),
      type: objA.type === objB.type ? objA.type : 'ellipse',
      x: bboxA.x + (bboxB.x - bboxA.x) * t,
      y: bboxA.y + (bboxB.y - bboxA.y) * t,
      w: bboxA.w + (bboxB.w - bboxA.w) * t,
      h: bboxA.h + (bboxB.h - bboxA.h) * t,
      fill: lerpColor(fillA, fillB, t),
      stroke: lerpColor(objA.stroke || '#0B1D3A', objB.stroke || '#0B1D3A', t),
      strokeWidth: (objA.strokeWidth || 1) + ((objB.strokeWidth || 1) - (objA.strokeWidth || 1)) * t,
      opacity: (objA.opacity || 1) + ((objB.opacity || 1) - (objA.opacity || 1)) * t,
      visible: true, locked: false, label: `Blend ${i}`,
      rx: objA.type === 'rect' ? ((objA.rx || 0) + ((objB.rx || 0) - (objA.rx || 0)) * t) : undefined,
    });
  }
  return results;
};

// ==================== PATTERN HELPERS ====================
export const createPattern = (id, tileW = 20, tileH = 20, patternType = 'dots') => {
  let content = '';
  switch (patternType) {
    case 'dots':
      content = `<circle cx="${tileW / 2}" cy="${tileH / 2}" r="2" fill="currentColor"/>`;
      break;
    case 'lines':
      content = `<line x1="0" y1="0" x2="${tileW}" y2="${tileH}" stroke="currentColor" stroke-width="1"/>`;
      break;
    case 'crosshatch':
      content = `<line x1="0" y1="0" x2="${tileW}" y2="${tileH}" stroke="currentColor" stroke-width="0.5"/><line x1="${tileW}" y1="0" x2="0" y2="${tileH}" stroke="currentColor" stroke-width="0.5"/>`;
      break;
    case 'grid':
      content = `<path d="M ${tileW} 0 L 0 0 0 ${tileH}" fill="none" stroke="currentColor" stroke-width="0.5"/>`;
      break;
    case 'diagonal':
      content = `<line x1="0" y1="${tileH}" x2="${tileW}" y2="0" stroke="currentColor" stroke-width="1"/>`;
      break;
    case 'hexagons': {
      const s = tileW / 3;
      content = `<polygon points="${s},0 ${2 * s},${tileH / 4} ${2 * s},${3 * tileH / 4} ${s},${tileH} 0,${3 * tileH / 4} 0,${tileH / 4}" fill="none" stroke="currentColor" stroke-width="0.5"/>`;
      break;
    }
    default: content = '';
  }
  return { id: id || `pat-${genId()}`, tileW, tileH, patternType, content };
};

export const PATTERN_TYPES = ['dots', 'lines', 'crosshatch', 'grid', 'diagonal', 'hexagons'];

// ==================== SYMBOL SYSTEM ====================
export const createSymbol = (name, objects) => ({
  id: genId(),
  name,
  objects: JSON.parse(JSON.stringify(objects)),
  created: Date.now()
});

export const instantiateSymbol = (symbol, x = 0, y = 0) => {
  return symbol.objects.map(obj => {
    const clone = { ...JSON.parse(JSON.stringify(obj)), id: genId() };
    if (clone.x !== undefined) { clone.x += x; clone.y += y; }
    if (clone.x1 !== undefined) { clone.x1 += x; clone.y1 += y; clone.x2 += x; clone.y2 += y; }
    return clone;
  });
};

// ==================== CLIPPING MASK ====================
export const createClipMask = (maskObjId, targetObjId) => ({
  maskId: maskObjId,
  targetId: targetObjId,
  clipPathId: `clip-${genId()}`
});

// ==================== MAGIC WAND (select by similarity) ====================
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '#000000');
  return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 0, g: 0, b: 0 };
};

const colorDistance = (c1, c2) => {
  const a = hexToRgb(c1), b = hexToRgb(c2);
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
};

export const selectBySimilarity = (objects, referenceObj, tolerance = 80) => {
  return objects.filter(o => {
    if (o.id === referenceObj.id) return true;
    const fillDist = colorDistance(o.fill || '#000', referenceObj.fill || '#000');
    const strokeDist = colorDistance(o.stroke || '#000', referenceObj.stroke || '#000');
    return fillDist < tolerance || strokeDist < tolerance;
  }).map(o => o.id);
};

// ==================== LASSO HELPERS ====================
export const isPointInPolygon = (px, py, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

export const selectByLasso = (objects, lassoPoints) => {
  if (lassoPoints.length < 3) return [];
  const polygon = lassoPoints.map(p => [p.x, p.y]);
  return objects.filter(o => {
    const cx = (o.x || 0) + (o.w || 0) / 2;
    const cy = (o.y || 0) + (o.h || 0) / 2;
    return isPointInPolygon(cx, cy, polygon);
  }).map(o => o.id);
};


// ==================== ANCHOR POINT TOOL ====================
// Converts shapes to editable paths with anchor points

export const shapeToAnchorPoints = (obj) => {
  if (obj.type === 'rect') {
    const { x, y, w, h } = obj;
    return [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ];
  }
  if (obj.type === 'ellipse') {
    const cx = obj.x + obj.w / 2, cy = obj.y + obj.h / 2;
    const rx = obj.w / 2, ry = obj.h / 2;
    const k = 0.5522847498;
    return [
      { x: cx, y: cy - ry, cx1: cx - rx * k, cy1: cy - ry, cx2: cx + rx * k, cy2: cy - ry },
      { x: cx + rx, y: cy, cx1: cx + rx, cy1: cy - ry * k, cx2: cx + rx, cy2: cy + ry * k },
      { x: cx, y: cy + ry, cx1: cx + rx * k, cy1: cy + ry, cx2: cx - rx * k, cy2: cy + ry },
      { x: cx - rx, y: cy, cx1: cx - rx, cy1: cy + ry * k, cx2: cx - rx, cy2: cy - ry * k },
    ];
  }
  if (obj.type === 'line') {
    return [
      { x: obj.x, y: obj.y },
      { x: obj.x + (obj.w || 0), y: obj.y + (obj.h || 0) },
    ];
  }
  if (obj.type === 'polygon' && obj.points) {
    return obj.points.split(/\s+/).map(p => {
      const [px, py] = p.split(',').map(Number);
      return { x: px, y: py };
    });
  }
  if (obj.type === 'path' && obj.d) {
    return parseSVGPath(obj.d);
  }
  return [];
};

export const anchorPointsToPath = (points, closed = true) => {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const hasPrevHandle = prev.cx2 !== undefined && prev.cy2 !== undefined;
    const hasCurrHandle = curr.cx1 !== undefined && curr.cy1 !== undefined;
    if (hasPrevHandle || hasCurrHandle) {
      const c1x = hasPrevHandle ? prev.cx2 : prev.x;
      const c1y = hasPrevHandle ? prev.cy2 : prev.y;
      const c2x = hasCurrHandle ? curr.cx1 : curr.x;
      const c2y = hasCurrHandle ? curr.cy1 : curr.y;
      d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${curr.x} ${curr.y}`;
    } else {
      d += ` L ${curr.x} ${curr.y}`;
    }
  }
  if (closed && points.length > 2) {
    const last = points[points.length - 1];
    const first = points[0];
    const hasLastHandle = last.cx2 !== undefined && last.cy2 !== undefined;
    const hasFirstHandle = first.cx1 !== undefined && first.cy1 !== undefined;
    if (hasLastHandle || hasFirstHandle) {
      const c1x = hasLastHandle ? last.cx2 : last.x;
      const c1y = hasLastHandle ? last.cy2 : last.y;
      const c2x = hasFirstHandle ? first.cx1 : first.x;
      const c2y = hasFirstHandle ? first.cy1 : first.y;
      d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${first.x} ${first.y}`;
    } else {
      d += ' Z';
    }
  }
  return d;
};

export const toggleAnchorPointSmooth = (points, index) => {
  const pt = points[index];
  if (!pt) return points;
  const newPoints = [...points];
  const hasHandles = pt.cx1 !== undefined || pt.cx2 !== undefined;
  if (hasHandles) {
    newPoints[index] = { x: pt.x, y: pt.y };
  } else {
    const prev = points[(index - 1 + points.length) % points.length];
    const next = points[(index + 1) % points.length];
    // Use 1/3 of neighbor distance for smoother curves (Illustrator-like)
    const dx = (next.x - prev.x) / 3;
    const dy = (next.y - prev.y) / 3;
    newPoints[index] = {
      x: pt.x, y: pt.y,
      cx1: pt.x - dx, cy1: pt.y - dy,
      cx2: pt.x + dx, cy2: pt.y + dy,
    };
  }
  return newPoints;
};

export const dragAnchorHandle = (points, index, handleType, newX, newY) => {
  // newX, newY are ABSOLUTE positions of the handle
  const pt = points[index];
  if (!pt) return points;
  const newPoints = [...points];
  if (handleType === 'out') {
    const cx2 = newX;
    const cy2 = newY;
    // Mirror the in-handle for smooth behavior
    const cx1 = pt.x - (cx2 - pt.x);
    const cy1 = pt.y - (cy2 - pt.y);
    newPoints[index] = { ...pt, cx1, cy1, cx2, cy2 };
  } else {
    const cx1 = newX;
    const cy1 = newY;
    // Mirror the out-handle
    const cx2 = pt.x - (cx1 - pt.x);
    const cy2 = pt.y - (cy1 - pt.y);
    newPoints[index] = { ...pt, cx1, cy1, cx2, cy2 };
  }
  return newPoints;
};

// ==================== CATMULL-ROM SPLINE → CUBIC BEZIER ====================
// Converts a series of points into smooth cubic bezier anchor points
// using Catmull-Rom interpolation with configurable tension
export const catmullRomToBezier = (points, tension = 0.3, closed = false) => {
  if (points.length < 2) return points.map(p => ({ x: p.x, y: p.y }));
  if (points.length === 2) return points.map(p => ({ x: p.x, y: p.y }));

  const result = [];
  const n = points.length;

  for (let i = 0; i < n; i++) {
    const p0 = points[(i - 1 + n) % n];
    const p1 = points[i];
    const p2 = points[(i + 1) % n];
    const p3 = points[(i + 2) % n];

    // For open paths, clamp endpoints
    const prev = (i === 0 && !closed) ? p1 : p0;
    const next = (i === n - 1 && !closed) ? p1 : p2;
    const next2 = (i >= n - 2 && !closed) ? next : p3;

    // Catmull-Rom tangent calculation
    const t = tension;
    const cx2 = p1.x + (next.x - prev.x) * t;
    const cy2 = p1.y + (next.y - prev.y) * t;
    const cx1 = p1.x - (next.x - prev.x) * t;
    const cy1 = p1.y - (next.y - prev.y) * t;

    // First and last points of an open path: no incoming/outgoing handle respectively
    if (i === 0 && !closed) {
      result.push({ x: p1.x, y: p1.y, cx2, cy2 });
    } else if (i === n - 1 && !closed) {
      result.push({ x: p1.x, y: p1.y, cx1, cy1 });
    } else {
      result.push({ x: p1.x, y: p1.y, cx1, cy1, cx2, cy2 });
    }
  }

  return result;
};
